import { useLayoutEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useLogoContrastBg } from "hooks/useLogoContrastBg";

import { remarkPlugins } from "components/MessageWithLinks";
import { transformLinkUri } from "components/MessageWithLinks/functions";
import renderers from "components/MessageWithLinks/renderers";
import { useGlobalsContext, useResizeObserver } from "shared/hooks";
import classNames from "shared/utils/classNames";
import { ReactComponent as IconClose } from "assets/images/icons/close-black.svg";

import StarterQuestionButton from "./StarterQuestionButton";
import { Props } from "./types";

function PopupMessagesNonIframe(props: Props) {
  const { widgetExpanded, setWidgetExpanded, parentPostMessage } =
    useGlobalsContext();

  const {
    botInfo,
    afterMessageQuestions,
    popupClosedByUser,
    widgetPosition,
    onSuggestionClick,
    setPopupClosedByUser,
    chatDataLength,
    isOverButton,
    welcomeMessage,
  } = props;

  const rootRef = useRef<HTMLDivElement>(null);
  const contentRect = useResizeObserver(rootRef);

  const logoUrl = botInfo.bot_info_config.company_logo;
  const inverseBg = useLogoContrastBg(logoUrl);
  const isFollowUp =
    afterMessageQuestions.length > 0 &&
    afterMessageQuestions[0].type === "follow-up-question";

  const hasSomethingToShow =
    botInfo.bot_info_config.show_floating_welcome_message ||
    (botInfo.bot_info_config.show_starter_widget && !!afterMessageQuestions) ||
    isFollowUp;

  const hidePopupMessages = isFollowUp
    ? false
    : popupClosedByUser ||
      (widgetExpanded && isOverButton) ||
      (isOverButton && !hasSomethingToShow) ||
      !!chatDataLength;

  useLayoutEffect(() => {
    if (widgetExpanded || !isOverButton) {
      return;
    }

    const sendAddCollapsedHeight = (height: number) =>
      parentPostMessage("add-collapsed-height", height);

    const newHeight = rootRef.current?.clientHeight
      ? rootRef.current.clientHeight + 25
      : 0;

    sendAddCollapsedHeight(newHeight);

    return () => sendAddCollapsedHeight(0);
  }, [
    parentPostMessage,
    widgetExpanded,
    isOverButton,
    contentRect,
    hidePopupMessages,
  ]);

  // Styles including inverse background and dynamic positioning
  const popupMessagesStyle = {
    "--boxShadowXOffset": widgetPosition === "left" ? "-25px" : "25px",
    "--alignMessages": widgetPosition === "left" ? "flex-start" : "flex-end",
  } as React.CSSProperties;

  if (hidePopupMessages) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={classNames(
        widgetExpanded ||
          (afterMessageQuestions && !afterMessageQuestions.length)
          ? "pb-0"
          : "pb-3",
        !widgetExpanded && "w-[280px]",
      )}
      style={popupMessagesStyle}
    >
      {!widgetExpanded &&
        botInfo.bot_info_config.show_floating_welcome_message && (
          <div
            className="welcome-message cursor-pointer"
            onClick={() => setWidgetExpanded(true)}
          >
            <div className="company-panel">
              <div
                className="flex items-center justify-center rounded-lg p-[1px]"
                style={{ background: inverseBg }}
              >
                <img
                  className="company-logo"
                  src={logoUrl}
                  alt="Company logo"
                />
              </div>
              <p className="grow overflow-auto break-words">
                {botInfo.bot_info_config.company_name}
              </p>
              {setPopupClosedByUser && (
                <button
                  className="close-button"
                  onClick={evt => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    parentPostMessage("add-collapsed-height", 0);
                    setPopupClosedByUser(true);
                  }}
                >
                  <IconClose />
                </button>
              )}
            </div>
            <ReactMarkdown
              remarkPlugins={remarkPlugins}
              transformLinkUri={transformLinkUri}
              components={renderers}
            >
              {welcomeMessage}
            </ReactMarkdown>
          </div>
        )}
      {(botInfo.bot_info_config.show_starter_widget ||
        isFollowUp ||
        (widgetExpanded && !isOverButton)) && (
        <div
          className="popup-messages-container"
          style={{
            flexDirection: widgetExpanded ? "row" : "column",
          }}
        >
          {afterMessageQuestions &&
            afterMessageQuestions.map(afterMessageQuestion => (
              <StarterQuestionButton
                key={
                  afterMessageQuestion.type === "follow-up-question"
                    ? afterMessageQuestion.question
                    : afterMessageQuestion.id
                }
                afterMessageQuestion={afterMessageQuestion}
                onSuggestionClick={onSuggestionClick}
                isOverButton={isOverButton}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export default PopupMessagesNonIframe;
